import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { formatDate } from '../../utils/dateUtils';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  Paper,
  useTheme,
  Chip,
  Autocomplete,
  TextField,
  DialogActions,
  Button,
  LinearProgress,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import PrintIcon from '@mui/icons-material/Print';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { 
  fetchPatientFiles,
  getPatientFileSignedUrl,
  scanPatientFolder,
  getLocalFilePreviewUrl,
  uploadPatientFile
} from '../../services/patientFileService';
import axios from 'axios';

const FILE_DESCRIPTION_OPTIONS = [
  "ΙΣΤΟΡΙΚΟ",
  "ΕΞΕΤΑΣΕΙΣ",
  "ΑΞΟΝΙΚΗ",
  "ΜΑΓΝΗΤΙΚΗ",
  "ΕΝΔΟΣΚΟΠΗΣΗ",
  "ΒΙΟΨΙΑ"
];

// Sub-component to render PDF thumbnails using pdf.js
const PdfThumbnail = ({ file, localApiClient }) => {
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const generateThumbnail = async () => {
      // Wait up to 2 seconds for pdfjsLib to initialize from the main script
      let pdfjs = window.pdfjsLib;
      if (!pdfjs) {
        for (let i = 0; i < 10; i++) {
          await new Promise(r => setTimeout(r, 200));
          pdfjs = window.pdfjsLib;
          if (pdfjs) break;
        }
      }

      if (!pdfjs) {
        setLoading(false);
        return;
      }

      try {
        // Clinical local preview logic: Local files must be served from localhost:5011
        const url = file.signedUrl || (file.is_local ? getLocalFilePreviewUrl(localApiClient, file.local_path) : null);
        if (!url) throw new Error('No preview source available');

        const loadingTask = window.pdfjsLib.getDocument({
          url,
          withCredentials: false // Essential for localhost cross-origin
        });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        
        // Render at a small scale for thumbnail
        const viewport = page.getViewport({ scale: 0.4 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        await page.render({ canvasContext: context, viewport }).promise;
        
        if (isMounted) {
          setThumbnail(canvas.toDataURL('image/jpeg', 0.7));
        }
        
        // Clean up
        pdf.destroy();
      } catch (err) {
        console.warn('[PDF] Thumbnail generation failed:', err.message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    generateThumbnail();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.local_path, file.is_local, file.signedUrl, localApiClient]); // Clinical preview dependencies

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!thumbnail) {
    return <PictureAsPdfIcon sx={{ fontSize: 64, color: 'error.main', opacity: 0.5 }} />;
  }

  return (
    <img 
      src={thumbnail} 
      alt={file.file_name} 
      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} 
    />
  );
};

// High-fidelity PDF Previewer for larger screens
const PdfFullPreview = ({ file, apiClient, scale, rotation }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renderProgress, setRenderProgress] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const renderPdf = async () => {
      let pdfjs = window.pdfjsLib;
      if (!pdfjs) {
        for (let i = 0; i < 10; i++) {
          await new Promise(r => setTimeout(r, 200));
          pdfjs = window.pdfjsLib;
          if (pdfjs) break;
        }
      }

      if (!pdfjs) return;

      try {
        // High-fidelity clinical preview logic: Prefer signed database URL over local filesystem path
        const url = file.signedUrl || (file.is_local ? getLocalFilePreviewUrl(apiClient, file.local_path) : null);
        if (!url) throw new Error('No preview source available');

        const loadingTask = pdfjs.getDocument(url);
        const pdf = await loadingTask.promise;
        const numPages = pdf.numPages;
        const renderedPages = [];
        
        for (let i = 1; i <= numPages; i++) {
          const page = await pdf.getPage(i);
          
          // Render at high resolution for preview
          const viewport = page.getViewport({ scale: 2.0 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          await page.render({ canvasContext: context, viewport }).promise;
          
          if (isMounted) {
            const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
            renderedPages.push(dataUrl);
            setRenderProgress(i / numPages * 100);
            
            // Show first page immediately for responsiveness
            if (i === 1) {
              setPages([dataUrl]);
            }
          }
        }
        
        if (isMounted) {
          setPages(renderedPages);
        }
        pdf.destroy();
      } catch (err) {
        console.error('[PDF Preview] Rendering failed:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    renderPdf();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.local_path, apiClient, file.is_local, file.signedUrl]); // Robust rendering dependencies

  if (loading && pages.length === 0) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
        <CircularProgress variant="determinate" value={renderProgress} />
        <Typography variant="caption">Rendering Clinical Pages... {Math.round(renderProgress)}%</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      bgcolor: 'black', 
      borderRadius: 1, 
      overflow: 'auto',
      height: '100%',
      width: '100%',
      p: 2,
      gap: 2,
      position: 'relative'
    }}>
      {pages.map((p, i) => (
        <Box 
          key={i} 
          sx={{ 
            boxShadow: 3, 
            bgcolor: 'white',
            lineHeight: 0,
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            transition: 'transform 0.2s ease-in-out',
            transformOrigin: 'center center'
          }}
        >
          <img 
            src={p} 
            alt={`Page ${i+1}`} 
            style={{ 
              maxWidth: '100%', 
              display: 'block'
            }} 
          />
        </Box>
      ))}
      {loading && (
        <Box sx={{ position: 'sticky', bottom: 10, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 2, py: 1, borderRadius: 5 }}>
          <Typography variant="caption">Loading more pages ({Math.round(renderProgress)}%)...</Typography>
        </Box>
      )}
    </Box>
  );
};

const PatientGalleryTab = ({ patientId, apiClient, folderPath }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [files, setFiles] = useState([]);
  const [dbFiles, setDbFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folderFound, setFolderFound] = useState(null); // null=unknown, true=found, false=not found
  const [previewFile, setPreviewFile] = useState(null);
  const [registeringFile, setRegisteringFile] = useState(null);
  const [regDescription, setRegDescription] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  
  // Auto-sync state
  const [autoUploadingIds, setAutoUploadingIds] = useState(new Set());
  const autoProcessingRef = useRef(new Set());

  // Image viewing state (zoom / rotate)
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);

  // High-fidelity local dedicated client for Gallery scanning on the clinical workstation
  const localApiClient = useMemo(() => {
    // Dynamic resolve the clinical server address
    // In multi-device offices, 'localhost' only works on the server machine itself.
    // We use the same host the app is currently loaded from (apiClient host).
    const baseUrl = apiClient?.defaults?.baseURL || `${window.location.origin}/api`;
    const instance = axios.create({
      baseURL: baseUrl
    });

    // Clinical Local Client: We bypass remote authentication headers for same-machine access if token is missing
    // but include it if available (hybrid auth) to ensure proper surgical record sync
    instance.interceptors.request.use(config => {
      const token = sessionStorage.getItem('token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    return instance;
  }, [apiClient]);

  const loadFolderFiles = useCallback(async () => {
    if (!folderPath) {
      setLoading(false);
      setFolderFound(false);
      return;
    }

    setLoading(true);
    
    try {
      // Fetch local folder scans from the MAC SERVER and database files from the REMOTE HUB
      const [scanResult, dbData] = await Promise.all([
        scanPatientFolder(localApiClient, folderPath).catch(err => {
          console.warn('[Files] Local Mac scanning failed:', err.message);
          return null; // null = network/server error
        }),
        apiClient ? fetchPatientFiles(apiClient, patientId).catch(() => []) : Promise.resolve([])
      ]);

      if (scanResult === null) {
        // Network/server error — folder unreachable
        setFolderFound(false);
        setFiles([]);
      } else {
        setFolderFound(scanResult.found !== false);
        setFiles(scanResult.files || []);
      }
      setDbFiles(dbData || []);
    } catch (err) {
      console.error('[Gallery] Failed to load clinical records:', err);
      setFolderFound(false);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, localApiClient, folderPath, patientId]);

  useEffect(() => {
    // Debug: Log the workstation path for clinical scanning
    if (folderPath) {
      console.log(`[Clinical-Workstation] Target Gallery Path: "${folderPath}"`);
    }
    loadFolderFiles();
  }, [loadFolderFiles, folderPath]);

  // Clinical Auto-Sync Rule: Automatically register "ΙΣΤΟΡΙΚΟ" files
  useEffect(() => {
    if (loading || files.length === 0) return;

    const targets = files.filter(file => {
      // Rule: Filename must contain "ΙΣΤΟΡΙΚΟ" (case-insensitive Greek)
      const nameUpper = String(file.file_name || '').toUpperCase();
      const isMatch = nameUpper.includes('ΙΣΤΟΡΙΚΟ');
      if (!isMatch) return false;

      // Rule: Must not be already registered in the Remote Hub database
      const isRegistered = dbFiles.some(db => 
        (db.filename === file.file_name || db.file_name === file.file_name) ||
        (db.storage_path === file.local_path)
      );
      
      // Rule: Must not be currently processing
      return !isRegistered && !autoProcessingRef.current.has(file.id);
    });

    if (targets.length === 0) return;

    // Start sequential background auto-sync
    const performAutoSync = async () => {
      console.log(`[Auto-Sync] Found ${targets.length} medical history files to register.`);
      
      for (const file of targets) {
        // Double check against concurrent runs
        if (autoProcessingRef.current.has(file.id)) continue;
        
        autoProcessingRef.current.add(file.id);
        setAutoUploadingIds(prev => new Set(prev).add(file.id));

        try {
          const localUrl = getLocalFilePreviewUrl(localApiClient, file.local_path);
          const response = await fetch(localUrl);
          if (!response.ok) throw new Error('Failed to fetch local file blob');
          const blob = await response.blob();
          
          const formData = new FormData();
          formData.append('file', blob, file.file_name);
          formData.append('original_path', file.local_path);
          formData.append('description', 'ΙΣΤΟΡΙΚΟ');

          const newDbFile = await uploadPatientFile(apiClient, patientId, formData);
          setDbFiles(prev => [newDbFile, ...prev]);
          console.log(`[Auto-Sync] Successfully registered: ${file.file_name}`);
        } catch (err) {
          console.error(`[Auto-Sync] Failed for ${file.file_name}:`, err.message);
        } finally {
          autoProcessingRef.current.delete(file.id);
          setAutoUploadingIds(prev => {
            const updated = new Set(prev);
            updated.delete(file.id);
            return updated;
          });
        }
      }
    };

    performAutoSync();
  }, [files, dbFiles, loading, localApiClient, apiClient, patientId]);

  // Debug: log the clinical scanning path for troubleshooting
  useEffect(() => {
    if (folderPath) {
      console.log(`[Gallery] Ready to scan clinical workstation folder: ${folderPath}`);
    }
  }, [folderPath]);

  const handlePreview = async (file) => {
    // Reset high-fidelity viewing state for new clinical photo
    setRotation(0);
    setScale(1);

    if (file.is_local) {
      // Secure local preview: Generate authorized stream URL from LOCAL MAC SERVER
      const url = getLocalFilePreviewUrl(localApiClient, file.local_path);
      setPreviewFile({
        ...file,
        signedUrl: url,
        signedFileType: file.file_type
      });
      return;
    }

    // Fallback for database records if any
    const fileId = file.id_file ?? file.id;
    if (!fileId) return;

    try {
      const signed = await getPatientFileSignedUrl(apiClient, fileId);
      if (signed?.url) {
        setPreviewFile({
          ...file,
          signedUrl: signed.url,
          signedFileType: signed.file_type || file.file_type
        });
      }
    } catch (err) {
      console.error('Failed to sign URL:', err);
    }
  };

  const handleNext = () => {
    if (!previewFile || !files.length) return;
    const currentIndex = files.findIndex(f => (f.id_file ?? f.id) === (previewFile.id_file ?? previewFile.id));
    const nextIndex = (currentIndex + 1) % files.length;
    handlePreview(files[nextIndex]);
  };

  const handlePrev = () => {
    if (!previewFile || !files.length) return;
    const currentIndex = files.findIndex(f => (f.id_file ?? f.id) === (previewFile.id_file ?? previewFile.id));
    const prevIndex = (currentIndex - 1 + files.length) % files.length;
    handlePreview(files[prevIndex]);
  };

  const handleRegisterClick = (e, file) => {
    e.stopPropagation();
    setRegisteringFile(file);
    setRegDescription('');
  };

  const handleConfirmRegister = async () => {
    if (!registeringFile) return;
    
    const file = registeringFile;
    setRegisteringFile(null);
    setUploadingId(file.id);
    
    try {
      // High-fidelity Remote Hub Registration: 
      // 1. Fetch file as blob from local Mac server
      // 2. Upload blob to the Remote Hub (Proxmox server)
      const localUrl = getLocalFilePreviewUrl(localApiClient, file.local_path);
      const response = await fetch(localUrl);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append('file', blob, file.file_name);
      formData.append('original_path', file.local_path);
      if (regDescription) {
        formData.append('description', regDescription);
      }

      const newDbFile = await uploadPatientFile(apiClient, patientId, formData);
      
      // Add the newly registered file to the local DB files tracking
      setDbFiles(prev => [newDbFile, ...prev]);
    } catch (err) {
      console.error('Remote registration failed:', err);
    } finally {
      setUploadingId(null);
    }
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
    setRotation(0);
    setScale(1);
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleReset = () => {
    setRotation(0);
    setScale(1);
  };

  const handlePrintImage = (file) => {
    const url = file.signedUrl;
    const win = window.open('', '_blank');
    if (!win) return;
    
    win.document.write(`
      <html>
        <head>
          <title>Print Clinical Photo - ${file.file_name}</title>
          <style>
            body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; background: #fff; }
            img { max-width: 100%; max-height: 100%; object-fit: contain; }
            @media print {
              body { margin: 0; }
              img { max-width: 100vw; max-height: 100vh; }
            }
          </style>
        </head>
        <body>
          <img src="${url}" onload="window.print();window.close();">
        </body>
      </html>
    `);
    win.document.close();
  };

  const renderFilePreview = (file) => {
    const type = String(file.signedFileType || file.file_type || '').toLowerCase();
    const url = file.signedUrl;

    if (type.startsWith('image/')) {
      return (
        <Box sx={{ 
          display: 'grid', 
          placeItems: 'safe center', // Modern high-fidelity center with overflow support
          bgcolor: 'black', 
          borderRadius: 1, 
          overflow: 'auto',
          height: '75vh',
          position: 'relative'
        }}>
          <img 
            src={url} 
            alt={file.file_name} 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain',
              transform: `scale(${scale}) rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease-in-out',
              transformOrigin: 'center center'
            }} 
          />
        </Box>
      );
    }

    if (type === 'application/pdf') {
      return (
        <Box sx={{ height: '75vh', bgcolor: 'black' }}>
          <PdfFullPreview 
            file={file} 
            apiClient={apiClient} 
            scale={scale} 
            rotation={rotation} 
          />
        </Box>
      );
    }

    return <Typography color="error">Unsupported file format for preview.</Typography>;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h6">Visual Record & Gallery</Typography>
          {folderPath && (
            <Tooltip title={`Scanning: ${folderPath}`}>
              <Chip
                size="small"
                icon={<FolderOpenIcon sx={{ fontSize: '0.9rem !important' }} />}
                label={folderPath.split('/').pop() || folderPath.split('\\').pop() || folderPath}
                variant="outlined"
                color={folderFound === false ? 'warning' : 'default'}
                sx={{ maxWidth: 220, fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}
          {autoUploadingIds.size > 0 && (
            <Fade in={true}>
              <Chip
                size="small"
                icon={<AutoFixHighIcon sx={{ fontSize: '0.9rem !important' }} />}
                label={`Auto-syncing ${autoUploadingIds.size} file(s)...`}
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
              />
            </Fade>
          )}
        </Stack>
        <IconButton onClick={loadFolderFiles} size="small" color="primary">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {autoUploadingIds.size > 0 && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {files.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
          {!folderPath ? (
            <>
              <FolderOpenIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="textSecondary" fontWeight="bold">No folder configured</Typography>
              <Typography variant="caption" color="textSecondary">
                Set the patient records base folder in Settings to enable the gallery.
              </Typography>
            </>
          ) : folderFound === false ? (
            <>
              <FolderOffIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1, opacity: 0.7 }} />
              <Typography color="textSecondary" fontWeight="bold">Folder not found on this workstation</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {folderPath}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                Check that the folder is mounted and the path is correct in Settings.
              </Typography>
            </>
          ) : (
            <Typography color="textSecondary">No photos or PDFs found in this folder.</Typography>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {files.map((file) => {
            const isPdf = String(file.file_type).toLowerCase() === 'application/pdf';
            const fileId = file.id; // Scanned files always have id
            const isRegistered = dbFiles.some(db => 
              (db.filename === file.file_name || db.file_name === file.file_name) ||
              (db.storage_path === file.local_path)
            );
            const isUploading = uploadingId === file.id || autoUploadingIds.has(file.id);
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={fileId}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', position: 'relative' }}>
                  
                  {/* Upload Actions Overlay */}
                  <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                    {isRegistered ? (
                      <Tooltip title="Already in EMR Database">
                        <CheckCircleOutlineIcon color="success" sx={{ bgcolor: 'background.paper', borderRadius: '50%' }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Add to Patient Files">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={(e) => handleRegisterClick(e, file)}
                          disabled={isUploading}
                          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' }, boxShadow: 1 }}
                        >
                          {isUploading ? <CircularProgress size={20} /> : <AddIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <CardActionArea onClick={() => handlePreview(file)}>
                    <Box sx={{ height: 320, position: 'relative', bgcolor: 'action.selected', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      {isPdf ? (
                        <PdfThumbnail file={file} localApiClient={localApiClient} />
                      ) : (file.signedUrl || file.is_local) ? (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                           <img 
                             src={getLocalFilePreviewUrl(localApiClient, file.local_path)} 
                             alt={file.file_name} 
                             style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                           />
                        </Box>
                      ) : (
                        <ImageIcon sx={{ fontSize: 64, color: 'primary.main', opacity: 0.5 }} />
                      )}
                    </Box>
                    <CardContent sx={{ flexGrow: 1, py: 1.5 }}>
                      <Typography variant="subtitle2" noWrap title={file.file_name} fontWeight="bold">
                        {file.file_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                        {formatDate(file.upload_date || file.created_at)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Popup Preview Dialog */}
      <Dialog 
        open={Boolean(previewFile)} 
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'background.paper', borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: '60%' }}>
            <Tooltip title="Previous (Left Arrow)">
              <IconButton size="small" onClick={handlePrev}>
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" component="div" noWrap>
              {previewFile?.file_name}
            </Typography>
            <Tooltip title="Next (Right Arrow)">
              <IconButton size="small" onClick={handleNext}>
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {previewFile && (String(previewFile.file_type).toLowerCase().startsWith('image/') || String(previewFile.file_type).toLowerCase() === 'application/pdf') && (
              <Stack direction="row" spacing={0.5} sx={{ mr: 2, borderRight: '1px solid', borderColor: 'divider', pr: 2 }}>
                <Tooltip title="Zoom Out">
                  <IconButton size="small" onClick={handleZoomOut}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'center', lineHeight: '30px' }}>
                  {Math.round(scale * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton size="small" onClick={handleZoomIn}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rotate 90°">
                  <IconButton size="small" onClick={handleRotate}>
                    <RotateRightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset View">
                  <IconButton size="small" onClick={handleReset}>
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print Photo">
                  <IconButton size="small" onClick={() => handlePrintImage(previewFile)}>
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            
            {previewFile?.signedUrl && (
              <Tooltip title="Download">
                <IconButton 
                  size="small" 
                  color="primary" 
                  component="a" 
                  href={previewFile.signedUrl} 
                  download={previewFile.file_name}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1, bgcolor: isDarkMode ? '#121212' : '#f5f5f5' }}>
          {previewFile && renderFilePreview(previewFile)}
        </DialogContent>
      </Dialog>

      {/* Registration Confirmation Dialog */}
      <Dialog 
        open={Boolean(registeringFile)} 
        onClose={() => setRegisteringFile(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add to Clinical Record</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Add <strong>{registeringFile?.file_name}</strong> to the patient's database files?
            </Typography>
            <Autocomplete
              freeSolo
              options={FILE_DESCRIPTION_OPTIONS}
              value={regDescription}
              onInputChange={(event, newValue) => setRegDescription(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Clinical Description / Category" 
                  fullWidth 
                  autoFocus
                  placeholder="Select or type category..." 
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisteringFile(null)}>Cancel</Button>
          <Button 
            onClick={handleConfirmRegister} 
            variant="contained" 
            color="primary"
          >
            Add to Database
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientGalleryTab;
